// frontend/src/pages/org/org-workspace-table.tsx
// Shared table for Workspaces (kind='team') and Customers (kind='customer').
// Enable/Disable All derives the POST kind from the kind prop so each surface
// scopes correctly with no duplication. Rows render through react-table
// (users-table.tsx pattern): filter only, no sorting.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  type Row,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link2, Loader2, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CustomerLinksDialog } from '@/components/onboarding/customer-links-dialog';
import { useFetch } from '@/hooks/use-fetch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useWorkspace } from '@/contexts/workspace-context';
import { useSession } from '@/contexts/session-context';
import { useIsMobile } from '@/hooks/use-mobile';
import { workspaceUrl } from '@/lib/workspace-url';
import { qk } from '@/lib/query/keys';
import { useDeleteWorkspace, useReclassifyWorkspace, useSetOrgForwarding } from './org-workspaces-queries';
import type { OrgWorkspaceRow } from './org-workspaces-queries';
import { unitTitle } from '@/lib/usage-unit';

export type { OrgWorkspaceRow } from './org-workspaces-queries';

interface OrgWorkspaceTableProps {
  /** All rows for this surface; the table filters them via `search`. */
  rows: OrgWorkspaceRow[];
  /** The meter every row's `used` is counted in — names the Usage column
   *  (AIT-436). Defaults to the legacy meter when the caller has no value. */
  usageUnit?: 'messages' | 'actions';
  /** Surface kind — drives the Enable All POST body. */
  kind: 'team' | 'customer';
  /**
   * Search query applied through react-table's global filter (matches
   * workspace name + each channel's name, IG handle, and phone — pretty and
   * digit-only). Omit to pass pre-filtered rows (Customers page).
   */
  search?: string;
  /**
   * Optional extra reload after a mutation, for callers whose list is not on
   * TanStack Query yet (Customers page). Query-backed callers rely on the
   * mutation hooks' invalidation instead.
   */
  onRefresh?: () => Promise<void>;
}

// Same forgiving token match the page's hand-rolled filter used: phone matches
// both "+972 55-704" and "97255704".
function workspaceGlobalFilter(
  row: Row<OrgWorkspaceRow>,
  _columnId: string,
  value: unknown,
): boolean {
  const q = String(value ?? '').trim().toLowerCase();
  if (!q) return true;
  const tokens: string[] = [row.original.name, row.original.publicId];
  if (row.original.externalId) tokens.push(row.original.externalId);
  for (const c of row.original.channels) {
    if (c.name) tokens.push(c.name);
    if (c.username) tokens.push(c.username);
    if (c.phone) {
      tokens.push(c.phone);
      tokens.push(c.phone.replace(/[+\s-]/g, ''));
    }
  }
  return tokens.join(' ').toLowerCase().includes(q);
}

// Cells whose clicks must not trigger the row's navigate.
const NON_NAVIGATING_COLUMNS = new Set(['select', 'enabled', 'actions', 'links']);

const CELL_CLASS_NAMES: Record<string, string> = {
  name: 'truncate font-medium',
  externalId: 'truncate',
};

export function OrgWorkspaceTable({ rows, kind, usageUnit, search, onRefresh }: OrgWorkspaceTableProps) {
  // Deprecated rows arrive in `rows` for admins (the pages fetch them) and are
  // hidden until the bulk-bar switch asks for them. Nothing to reveal means
  // nothing to toggle, so the switch disables itself.
  const [showDeprecated, setShowDeprecated] = useState(false);
  const hasDeprecated = rows.some((r) => r.deprecated);
  // Memoized: react-table re-derives its row model (and remounts every cell)
  // whenever `data` changes identity, so a fresh array per render churns rows
  // out from under an in-flight click.
  const visibleRows = useMemo(
    () => (showDeprecated ? rows : rows.filter((r) => !r.deprecated)),
    [rows, showDeprecated],
  );
  const { orgId } = useParams<{ orgId: string }>();
  const { refreshWorkspaces } = useWorkspace();
  const { role } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  // Mobile: a single tap on a row can be the start of a horizontal scroll, so
  // navigating on first tap is annoying. Require a double-tap instead; desktop
  // keeps single-click. lastTapRef tracks the previous tap's row + timestamp.
  const lastTapRef = useRef<{ id: string; time: number } | null>(null);

  const activateRow = useCallback(
    (publicId: string) => {
      // Thread the originating route so the customer breadcrumb's org crumb can
      // return here (defaults to org Channels otherwise).
      navigate(workspaceUrl(publicId), { state: { from: location.pathname } });
    },
    [navigate, location.pathname],
  );

  const handleRowClick = useCallback(
    (publicId: string) => {
      if (!isMobile) {
        activateRow(publicId);
        return;
      }
      const now = Date.now();
      const last = lastTapRef.current;
      if (last && last.id === publicId && now - last.time < 400) {
        lastTapRef.current = null;
        activateRow(publicId);
      } else {
        lastTapRef.current = { id: publicId, time: now };
      }
    },
    [isMobile, activateRow],
  );

  const isOrgAdmin = role === 'admin';
  const targetKind = kind === 'team' ? 'customer' : 'team';
  const reclassifyLabel = kind === 'team' ? 'Move to Customers' : 'Move to Workspaces';
  // Kind-aware noun so toasts + confirm copy read "customer(s)" on the Customers
  // surface and "workspace(s)" on the Workspaces surface.
  const noun = kind === 'customer' ? 'customer' : 'workspace';
  const nounPlural = `${noun}s`;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Optimistic forwarding overlay keyed by publicId: flip the row switches the
  // instant the toggle is clicked, then reconcile with the server refetch (the
  // rows prop) in onSuccess/onError. Without this the switches only move after
  // the round-trip completes.
  const [optimisticForwarding, setOptimisticForwarding] = useState<Map<string, boolean>>(
    new Map(),
  );
  const [disableConfirm, setDisableConfirm] = useState<{
    workspaceIds: string[];
    count: number;
  } | null>(null);
  // Delete/Deprecate confirm. `mode` is the UI's GUESS from row counts — the
  // server decides the real outcome; the toast trusts the response (D5).
  const [deleteConfirm, setDeleteConfirm] = useState<{
    row: OrgWorkspaceRow;
    mode: 'delete' | 'deprecate';
  } | null>(null);
  const fetchWithAuth = useFetch();
  // Per-customer onboarding-links modal (Customers surface only).
  const [linksCustomer, setLinksCustomer] = useState<{ publicId: string; name: string } | null>(
    null,
  );
  // Active onboarding-link count per customer, so the row button reads
  // "Generate" (none yet) vs "View Links" (has some). Customers surface only.
  const [linkCounts, setLinkCounts] = useState<Map<string, number>>(new Map());

  const loadLinkCounts = useCallback(async () => {
    if (kind !== 'customer') return;
    try {
      const res = await fetchWithAuth('/org/onboarding-links');
      if (!res.ok) return;
      const data = (await res.json()) as {
        links: { status: string; targetWorkspace: { publicId: string } | null }[];
      };
      const counts = new Map<string, number>();
      for (const l of data.links) {
        if (l.status === 'active' && l.targetWorkspace) {
          counts.set(l.targetWorkspace.publicId, (counts.get(l.targetWorkspace.publicId) ?? 0) + 1);
        }
      }
      setLinkCounts(counts);
    } catch {
      // Non-fatal: the button just defaults to "Generate" until counts load.
    }
  }, [kind, fetchWithAuth]);

  useEffect(() => {
    void loadLinkCounts();
  }, [loadLinkCounts]);

  const setOrgForwarding = useSetOrgForwarding(orgId);
  const reclassifyWorkspace = useReclassifyWorkspace(orgId);
  const { mutate: mutateForwarding, isPending: forwardingBusy } = setOrgForwarding;
  const { mutate: mutateReclassify } = reclassifyWorkspace;
  const { mutate: mutateDelete, isPending: deleteBusy } = useDeleteWorkspace(orgId);

  const toggleSelected = useCallback((publicId: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(publicId);
      else next.delete(publicId);
      return next;
    });
  }, []);

  // ponytail: Enable All sends { enabled, kind }; per-workspace sends { enabled, workspaceIds }.
  const setForwarding = useCallback(
    (workspaceIds: string[] | null, enabled: boolean) => {
      if (!orgId) return;
      // Flip the affected switches immediately; null = every row of this kind.
      // Only rows with a connected channel are eligible (the backend enables
      // channels, so a channel-less row can't change) — this keeps zero-channel
      // rows from flickering on/off during Enable All.
      setOptimisticForwarding((prev) => {
        const next = new Map(prev);
        const eligible = new Set(
          rows.filter((r) => r.connectedChannelCount > 0 && !r.deprecated).map((r) => r.publicId),
        );
        const targets = (workspaceIds ?? [...eligible]).filter((id) => eligible.has(id));
        for (const id of targets) next.set(id, enabled);
        return next;
      });
      mutateForwarding(
        { enabled, workspaceIds, kind },
        {
          onSuccess: async () => {
            const label =
              workspaceIds === null
                ? `All ${nounPlural}`
                : workspaceIds.length === 1
                ? noun.charAt(0).toUpperCase() + noun.slice(1)
                : `${workspaceIds.length} ${nounPlural}`;
            toast.success(enabled ? `${label} enabled` : `${label} disabled`);
            setSelected(new Set());
            await onRefresh?.();
            await refreshWorkspaces();
            setOptimisticForwarding(new Map());
          },
          onError: (err) => {
            setOptimisticForwarding(new Map());
            toast.error(err.userMessage ?? 'Failed to update messaging');
          },
        },
      );
    },
    [orgId, mutateForwarding, kind, noun, nounPlural, rows, onRefresh, refreshWorkspaces],
  );

  // Rename dialog: target row + draft name. PATCH /workspaces/:id, then reload
  // the caller's table AND the app-wide workspace list (sidebar, pickers).
  const [renameTarget, setRenameTarget] = useState<{ publicId: string; name: string } | null>(
    null,
  );
  const [renameDraft, setRenameDraft] = useState('');
  const [renaming, setRenaming] = useState(false);
  const queryClient = useQueryClient();
  const submitRename = useCallback(async () => {
    if (!renameTarget) return;
    const name = renameDraft.trim();
    if (!name || name === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    setRenaming(true);
    try {
      const res = await fetchWithAuth(`/workspaces/${renameTarget.publicId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': renameTarget.publicId },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        toast.success(`Renamed to "${name}"`);
        setRenameTarget(null);
        // Reload every surface that shows the name: this table, the app-wide
        // workspace list, and the TanStack-cached org workspace/customer lists
        // (connect-modal picker, org pages).
        await Promise.all([
          onRefresh?.(),
          refreshWorkspaces(),
          orgId
            ? queryClient.invalidateQueries({ queryKey: qk.org(orgId, 'workspaces') })
            : Promise.resolve(),
        ]);
      } else {
        toast.error(`Could not rename this ${noun}.`);
      }
    } catch {
      // Network failure — fetchWithAuth rejects; without this catch the
      // `void submitRename()` call site turns it into an unhandled rejection
      // with no user feedback.
      toast.error(`Could not rename this ${noun}.`);
    } finally {
      setRenaming(false);
    }
  }, [renameTarget, renameDraft, fetchWithAuth, onRefresh, refreshWorkspaces, noun, orgId, queryClient]);

  const reclassify = useCallback(
    (row: OrgWorkspaceRow) => {
      if (!orgId) return;
      mutateReclassify(
        { workspaceId: row.publicId, kind: targetKind },
        {
          onSuccess: async () => {
            await onRefresh?.();
          },
          onError: (err) => {
            toast.error(err.userMessage ?? 'Could not move this workspace.');
          },
        },
      );
    },
    [orgId, mutateReclassify, targetKind, onRefresh],
  );

  const confirmDelete = useCallback(() => {
    if (!deleteConfirm) return;
    const { row } = deleteConfirm;
    mutateDelete(
      { workspaceId: row.publicId },
      {
        onSuccess: async (res) => {
          // Trust the server's outcome, not the menu label the user clicked.
          toast.success(res.result === 'deleted' ? `${row.name} deleted` : `${row.name} deprecated`);
          setDeleteConfirm(null);
          await onRefresh?.();
          await refreshWorkspaces();
        },
        onError: (err) => {
          toast.error(err.userMessage ?? 'Could not delete this workspace.');
        },
      },
    );
  }, [deleteConfirm, mutateDelete, onRefresh, refreshWorkspaces]);

  const columns = useMemo<ColumnDef<OrgWorkspaceRow>[]>(
    () => [
      {
        id: 'select',
        header: () => null,
        cell: ({ row }) =>
          row.original.deprecated ? null : (
            <Checkbox
              aria-label={`Select ${row.original.name}`}
              checked={selected.has(row.original.publicId)}
              onCheckedChange={(checked) =>
                toggleSelected(row.original.publicId, checked === true)
              }
            />
          ),
        enableGlobalFilter: false,
      },
      // Customer public id first (Kapso "ID" column) — copy-friendly, searchable.
      ...(kind === 'customer'
        ? ([
            {
              id: 'publicId',
              header: 'ID',
              cell: ({ row }) => (
                <span className="font-mono text-xs text-muted-foreground">
                  {row.original.publicId}
                </span>
              ),
              enableGlobalFilter: false,
            },
          ] satisfies ColumnDef<OrgWorkspaceRow>[])
        : []),
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) =>
          row.original.deprecated ? (
            <span className="inline-flex items-center gap-2">
              {row.original.name}
              <Badge variant="secondary">Deprecated</Badge>
            </span>
          ) : (
            row.original.name
          ),
      },
      // Customer-only columns (Kapso parity): the reseller's own identifier and
      // the per-customer onboarding-links modal.
      ...(kind === 'customer'
        ? ([
            {
              id: 'externalId',
              header: 'External ID',
              cell: ({ row }) =>
                row.original.externalId ?? <span className="text-muted-foreground">-</span>,
            },
            {
              id: 'links',
              header: 'Onboarding Links',
              cell: ({ row }) => {
                if (row.original.deprecated) return null;
                const hasLinks = (linkCounts.get(row.original.publicId) ?? 0) > 0;
                return (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={
                      hasLinks
                        ? `View onboarding links for ${row.original.name}`
                        : `Generate onboarding link for ${row.original.name}`
                    }
                    onClick={() =>
                      setLinksCustomer({
                        publicId: row.original.publicId,
                        name: row.original.name,
                      })
                    }
                  >
                    <Link2 className="size-3.5" />
                    {hasLinks ? 'View Links' : 'Generate'}
                  </Button>
                );
              },
              enableGlobalFilter: false,
            },
          ] satisfies ColumnDef<OrgWorkspaceRow>[])
        : []),
      {
        id: 'channels',
        header: 'Channels',
        cell: ({ row }) => row.original.channelCount,
      },
      {
        id: 'usage',
        header: unitTitle(usageUnit),
        cell: ({ row }) => row.original.used.toLocaleString(),
      },
      {
        id: 'enabled',
        header: 'Enabled',
        cell: ({ row }) => (
          <Tooltip>
            {/* Disabled switches swallow pointer events, so the
                span wrapper is the hover target for the tooltip. */}
            <TooltipTrigger render={<span className="inline-flex" />}>
              <Switch
                size="sm"
                aria-label={`Toggle ${row.original.name}`}
                checked={
                  optimisticForwarding.get(row.original.publicId) ??
                  row.original.forwardingEnabled
                }
                disabled={
                  forwardingBusy ||
                  row.original.deprecated ||
                  row.original.connectedChannelCount === 0
                }
                onCheckedChange={(checked) =>
                  checked
                    ? setForwarding([row.original.publicId], true)
                    : setDisableConfirm({ workspaceIds: [row.original.publicId], count: 1 })
                }
              />
            </TooltipTrigger>
            {row.original.connectedChannelCount === 0 && (
              <TooltipContent>
                Connect a channel to this workspace to enable messaging.
              </TooltipContent>
            )}
          </Tooltip>
        ),
        enableGlobalFilter: false,
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) =>
          isOrgAdmin &&
          !row.original.deprecated && (
            <span className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Actions for ${row.original.name}`}
                    />
                  }
                >
                  <MoreVertical className="size-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {/* Onboarding-link generation lives in the Onboarding Links
                      column now, not here. */}
                  <DropdownMenuItem
                    onClick={() => {
                      setRenameDraft(row.original.name);
                      setRenameTarget({
                        publicId: row.original.publicId,
                        name: row.original.name,
                      });
                    }}
                  >
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => reclassify(row.original)}>
                    {reclassifyLabel}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() =>
                      setDeleteConfirm({
                        row: row.original,
                        // Label guess only — counts say "never used"; the
                        // server still decides (soft-deleted channels etc.).
                        mode:
                          row.original.channelCount === 0 && row.original.used === 0
                            ? 'delete'
                            : 'deprecate',
                      })
                    }
                  >
                    {row.original.channelCount === 0 && row.original.used === 0
                      ? 'Delete workspace'
                      : 'Deprecate workspace'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          ),
        enableGlobalFilter: false,
      },
    ],
    [selected, toggleSelected, forwardingBusy, optimisticForwarding, setForwarding, isOrgAdmin, reclassify, reclassifyLabel, kind, linkCounts, usageUnit],
  );

  const table = useReactTable({
    data: visibleRows,
    columns,
    getRowId: (row) => row.publicId,
    state: { globalFilter: search ?? '' },
    globalFilterFn: workspaceGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const tableRows = table.getRowModel().rows;

  const selectableRows = tableRows.filter((r) => !r.original.deprecated);

  const allSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.original.publicId));

  return (
    <>
      {/* Bulk bar: per-row toggles flip one workspace; Select All + these
          buttons act on many at once. Enable All targets every workspace
          of this kind in the org (workspaceIds omitted, kind sent). */}
      <div className="flex flex-wrap items-center gap-2">
        {selected.size > 0 && (
          <>
            <span className="text-sm text-muted-foreground">
              {selected.size} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={forwardingBusy}
              onClick={() => setForwarding([...selected], true)}
            >
              Enable Selected
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={forwardingBusy}
              onClick={() =>
                setDisableConfirm({ workspaceIds: [...selected], count: selected.size })
              }
            >
              Disable Selected
            </Button>
          </>
        )}
        {isOrgAdmin && (
          <div className="flex items-center gap-2 sm:ml-auto">
            <Switch
              aria-label="Show deprecated"
              checked={showDeprecated}
              onCheckedChange={setShowDeprecated}
              disabled={!hasDeprecated}
            />
            <span className="text-sm text-muted-foreground">Show deprecated</span>
          </div>
        )}
        <Button
          size="sm"
          variant="outline"
          className={isOrgAdmin ? undefined : 'sm:ml-auto'}
          onClick={() =>
            setSelected(
              allSelected
                ? new Set()
                : new Set(selectableRows.map((r) => r.original.publicId)),
            )
          }
        >
          {allSelected ? 'Clear' : 'Select All'}
        </Button>
        <Button
          size="sm"
          disabled={forwardingBusy}
          onClick={() => setForwarding(null, true)}
        >
          Enable All
        </Button>
      </div>

      {/* No flex-1 on the Card: it hugs its rows and only shrinks (min-h-0)
          when they would overflow the viewport, scrolling the table
          internally. */}
      <Card className="min-h-0 !overflow-visible">
        <CardContent className="flex min-h-0 flex-1 flex-col">
          <div className="scrollable-table min-h-0 flex-1 md:[&_[data-slot=table-container]]:h-full max-md:[&_[data-slot=table-container]]:max-h-[500px]">
            <Table className="table-fixed max-md:min-w-[44rem]">
              {kind === 'customer' ? (
                // Sized so every nowrap header AND the mono ws_ id fit at the
                // 44rem mobile min-width (704px) without bleeding into the
                // next column: Onboarding Links needs ~123px (18%), Channels
                // ~69px (10%), Enabled ~61px (9%), the id ~95px (14%).
                <colgroup>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '6%' }} />
                </colgroup>
              ) : (
                <colgroup>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '38%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
              )}
              <TableHeader className="sticky top-0 z-10 bg-card">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {tableRows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={row.original.deprecated ? 'opacity-60' : 'cursor-pointer'}
                    onClick={() => {
                      if (!row.original.deprecated) handleRowClick(row.original.publicId);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={CELL_CLASS_NAMES[cell.column.id]}
                        onClick={
                          NON_NAVIGATING_COLUMNS.has(cell.column.id)
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {tableRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center text-muted-foreground">
                      No workspaces match your search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={disableConfirm !== null}
        onOpenChange={(open) => {
          if (!forwardingBusy && !open) setDisableConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disable messaging for{' '}
              {disableConfirm?.count === 1
                ? `this ${noun}`
                : `${disableConfirm?.count ?? 0} ${nounPlural}`}
              ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sending and receiving stops immediately for every
              connected channel in{' '}
              {disableConfirm?.count === 1 ? `this ${noun}` : `these ${nounPlural}`}.
              Any messages received while disabled are permanently lost. You can
              re-enable them at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Messaging</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={forwardingBusy}
              onClick={() => {
                if (!disableConfirm) return;
                const ids = disableConfirm.workspaceIds;
                setDisableConfirm(null);
                setForwarding(ids, false);
              }}
            >
              {forwardingBusy && <Loader2 className="size-3.5 animate-spin" />}
              {forwardingBusy ? 'Disabling...' : 'Disable Messaging'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => {
          if (!deleteBusy && !open) setDeleteConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteConfirm?.mode === 'delete'
                ? `Delete ${deleteConfirm.row.name}?`
                : `Deprecate ${deleteConfirm?.row.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.mode === 'delete'
                ? 'This workspace has never been used, so it will be removed permanently.'
                : 'The workspace is hidden from lists and its channels are disconnected. Its history is kept.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleteBusy}
              onClick={confirmDelete}
            >
              {deleteBusy && <Loader2 className="size-3.5 animate-spin" />}
              {deleteConfirm?.mode === 'delete' ? 'Delete Workspace' : 'Deprecate Workspace'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CustomerLinksDialog
        customer={linksCustomer}
        onClose={() => {
          setLinksCustomer(null);
          // A generate/revoke inside the modal changes the count → relabel rows.
          void loadLinkCounts();
        }}
      />

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!renaming && !open) setRenameTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename {noun}</DialogTitle>
          </DialogHeader>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submitRename();
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="rename-workspace-name">Name</Label>
              <Input
                id="rename-workspace-name"
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                autoFocus
                maxLength={100}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={renaming}
                onClick={() => setRenameTarget(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={renaming || !renameDraft.trim()}>
                {renaming && <Loader2 className="size-3.5 animate-spin" />}
                Rename
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
